// util-partners
import { onDestroy } from "@/modules/_";
import { Raf, Resize } from "@lib/subs";
import { Scroll } from "@lib/scroll";

const BASE_SPEED = 42;
const DEFAULT_DIRECTION: 1 | -1 = -1;

const VELOCITY_LERP_HZ = 4;
const BOOST_DECAY_HZ = 2.4;

const SCROLL_BOOST_GAIN = 1.2;
const SCROLL_BOOST_CAP = 1400;

const SCROLL_DIR_THRESHOLD = 0.6;

const DRAG_THRESHOLD = 4;
const DRAG_VELOCITY_EMA_MS = 80;

export default function (element: HTMLElement, dataset: DOMStringMap) {
  const wrapEl = element.querySelector<HTMLElement>(
    '[util-partners="cms-list-wrap"]',
  );
  const listEl = element.querySelector<HTMLElement>(
    '[util-partners="cms-list"]',
  );
  if (!wrapEl || !listEl) return;
  const wrap: HTMLElement = wrapEl;
  const list: HTMLElement = listEl;

  const items = Array.from(
    list.querySelectorAll<HTMLElement>('[util-partners="cms-item"]'),
  );
  if (items.length === 0) return;
  const images = Array.from(list.querySelectorAll<HTMLImageElement>("img"));

  const prev = {
    wrapOverflow: wrap.style.overflow,
    wrapCursor: wrap.style.cursor,
    wrapTouchAction: wrap.style.touchAction,
    wrapUserSelect: wrap.style.userSelect,
    listDisplay: list.style.display,
    listFlexWrap: list.style.flexWrap,
    listWidth: list.style.width,
    listWillChange: list.style.willChange,
    listTransform: list.style.transform,
  };

  wrap.style.overflow = "hidden";
  wrap.style.cursor = "grab";
  wrap.style.touchAction = "pan-y";
  wrap.style.userSelect = "none";

  list.style.display = "flex";
  list.style.flexWrap = "nowrap";
  list.style.width = "max-content";
  list.style.willChange = "transform";

  const prevItemFlex = items.map((i) => i.style.flexShrink);
  for (const item of items) item.style.flexShrink = "0";
  const prevImgPointerEvents = images.map((img) => img.style.pointerEvents);
  const prevImgDraggable = images.map((img) => img.getAttribute("draggable"));
  for (const img of images) {
    img.style.pointerEvents = "none";
    img.setAttribute("draggable", "false");
  }

  let originalWidth = 0;
  const clones: HTMLElement[] = [];

  function measureOriginal() {
    const first = items[0];
    const last = items[items.length - 1];
    if (!first || !last) {
      originalWidth = 0;
      return;
    }
    // offsetLeft span includes flex gaps/margins; summing widths would miss them and cause seam pops.
    originalWidth =
      last.offsetLeft - first.offsetLeft + last.getBoundingClientRect().width;
  }

  function rebuildClones() {
    for (const c of clones) c.remove();
    clones.length = 0;
    if (originalWidth <= 0) return;
    const vw = wrap.clientWidth;
    // +1 extra cycle so high-momentum frames never outrun the track and expose a gap.
    const need = Math.max(1, Math.ceil(vw / originalWidth) + 1);
    for (let i = 0; i < need; i++) {
      for (const item of items) {
        const c = item.cloneNode(true) as HTMLElement;
        c.setAttribute("aria-hidden", "true");
        c.style.flexShrink = "0";
        c.querySelectorAll("img").forEach((img) => {
          img.style.pointerEvents = "none";
          img.setAttribute("draggable", "false");
        });
        // Remove clones from tab order — they're decorative duplicates.
        c.querySelectorAll("a, button, [tabindex]").forEach((el) =>
          el.setAttribute("tabindex", "-1"),
        );
        list.appendChild(c);
        clones.push(c);
      }
    }
  }

  measureOriginal();
  rebuildClones();

  let x = 0;
  let vx = BASE_SPEED * DEFAULT_DIRECTION;
  let directionSign: 1 | -1 = DEFAULT_DIRECTION;
  let scrollBoost = 0;

  function targetVx(): number {
    return directionSign * (BASE_SPEED + scrollBoost);
  }

  function applyTransform() {
    if (originalWidth > 0) {
      x = ((x % originalWidth) + originalWidth) % originalWidth;
      if (x > 0) x -= originalWidth;
    }
    list.style.transform = `translate3d(${x}px, 0, 0)`;
  }
  applyTransform();

  const offScroll = Scroll.add(
    ({ velocity }: { velocity: number }) => {
      if (Math.abs(velocity) > SCROLL_DIR_THRESHOLD) {
        directionSign = velocity > 0 ? -1 : 1;
      }
      const add = Math.min(
        SCROLL_BOOST_CAP,
        Math.abs(velocity) * SCROLL_BOOST_GAIN,
      );
      if (add > scrollBoost) scrollBoost = add;
    },
  );

  let isDown = false;
  let isDragging = false;
  let pointerId: number | null = null;
  let startPx = 0;
  let lastPx = 0;
  let lastT = 0;
  let dragVel = 0;

  function onPointerDown(e: PointerEvent) {
    if (isDown) return;
    isDown = true;
    isDragging = false;
    pointerId = e.pointerId;
    startPx = lastPx = e.clientX;
    lastT = performance.now();
    dragVel = 0;
    try {
      wrap.setPointerCapture(e.pointerId);
    } catch {
      // Pointer capture is best-effort; window listeners cover the rest.
    }
    window.addEventListener("pointermove", onPointerMove);
    window.addEventListener("pointerup", onPointerUp);
    window.addEventListener("pointercancel", onPointerUp);
  }

  function onPointerMove(e: PointerEvent) {
    if (!isDown || e.pointerId !== pointerId) return;
    const dx = e.clientX - lastPx;
    if (!isDragging) {
      if (Math.abs(e.clientX - startPx) < DRAG_THRESHOLD) {
        lastPx = e.clientX;
        lastT = performance.now();
        return;
      }
      isDragging = true;
      wrap.style.cursor = "grabbing";
    }
    const now = performance.now();
    const dt = Math.max(1, now - lastT);
    x += dx;
    const inst = (dx / dt) * 1000;
    const alpha = Math.min(1, dt / DRAG_VELOCITY_EMA_MS);
    dragVel = dragVel * (1 - alpha) + inst * alpha;
    lastPx = e.clientX;
    lastT = now;
    applyTransform();
  }

  function onPointerUp(e: PointerEvent) {
    if (e.pointerId !== pointerId) return;
    endDrag();
  }

  function endDrag() {
    if (!isDown) return;
    isDown = false;
    window.removeEventListener("pointermove", onPointerMove);
    window.removeEventListener("pointerup", onPointerUp);
    window.removeEventListener("pointercancel", onPointerUp);
    if (pointerId !== null && wrap.hasPointerCapture(pointerId)) {
      wrap.releasePointerCapture(pointerId);
    }
    pointerId = null;
    wrap.style.cursor = "grab";
    if (!isDragging) return;
    isDragging = false;
    if (Math.abs(dragVel) > 1) {
      directionSign = dragVel >= 0 ? 1 : -1;
    }
    // Seed vx from drag so velocity lerp eases back to baseline instead of snapping.
    vx = dragVel;
    dragVel = 0;
  }

  function onLostCapture() {
    endDrag();
  }

  function onClickCapture(e: MouseEvent) {
    if (Math.abs(e.clientX - startPx) >= DRAG_THRESHOLD) {
      e.stopPropagation();
      e.preventDefault();
    }
  }

  wrap.addEventListener("pointerdown", onPointerDown);
  wrap.addEventListener("lostpointercapture", onLostCapture);
  wrap.addEventListener("click", onClickCapture, { capture: true });

  let lastFrameT = performance.now();
  const offRaf = Raf.add(() => {
    const now = performance.now();
    let dt = (now - lastFrameT) / 1000;
    lastFrameT = now;
    // Cap dt so a backgrounded tab resuming doesn't teleport the marquee one viewport in one frame.
    if (dt > 0.1) dt = 0.1;

    const boostDecay = 1 - Math.exp(-BOOST_DECAY_HZ * dt);
    scrollBoost += (0 - scrollBoost) * boostDecay;
    if (scrollBoost < 0.1) scrollBoost = 0;

    if (!isDragging) {
      const lerp = 1 - Math.exp(-VELOCITY_LERP_HZ * dt);
      vx += (targetVx() - vx) * lerp;
      x += vx * dt;
      applyTransform();
    }
  });

  const offResize = Resize.add(() => {
    measureOriginal();
    rebuildClones();
    applyTransform();
  });

  onDestroy(() => {
    offRaf();
    offResize();
    offScroll();
    endDrag();
    wrap.removeEventListener("pointerdown", onPointerDown);
    wrap.removeEventListener("lostpointercapture", onLostCapture);
    wrap.removeEventListener("click", onClickCapture, { capture: true });

    for (const c of clones) c.remove();
    clones.length = 0;

    items.forEach((item, i) => {
      item.style.flexShrink = prevItemFlex[i];
    });
    images.forEach((img, i) => {
      img.style.pointerEvents = prevImgPointerEvents[i];
      const prevDraggable = prevImgDraggable[i];
      if (prevDraggable === null) {
        img.removeAttribute("draggable");
      } else {
        img.setAttribute("draggable", prevDraggable);
      }
    });

    wrap.style.overflow = prev.wrapOverflow;
    wrap.style.cursor = prev.wrapCursor;
    wrap.style.touchAction = prev.wrapTouchAction;
    wrap.style.userSelect = prev.wrapUserSelect;
    list.style.display = prev.listDisplay;
    list.style.flexWrap = prev.listFlexWrap;
    list.style.width = prev.listWidth;
    list.style.willChange = prev.listWillChange;
    list.style.transform = prev.listTransform;
  });
}
